import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Checkbox,
    Chip,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MODES = ['online', 'in-person', 'hybrid'];
// Display labels — the underlying enum value stays lowercase for the DB.
const MODE_LABEL = {
    online: 'Online',
    'in-person': 'In-person',
    hybrid: 'Hybrid',
};

const isYearWithTopics = (yg) => yg === 'Year 10' || yg === 'Year 11' || yg === 'Year 12' || yg === 'Year 13';

const studentLabel = (s) => {
    if (!s) return '';
    const me = `${s.firstName || ''} ${s.surname || ''}`.trim();
    if (s.role === 'parent' && s.childInfo?.studentName) {
        return `${me} (parent of ${s.childInfo.studentName})`;
    }
    return me;
};

export default function TutoringClassFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [students, setStudents] = useState([]);
    const [topics, setTopics] = useState([]);
    const [subjects, setSubjects] = useState([]);

    const [name, setName] = useState('');
    const [academicYearId, setAcademicYearId] = useState('');
    const [termIds, setTermIds] = useState([]);
    const [tutorId, setTutorId] = useState('');
    const [studentIds, setStudentIds] = useState([]);
    const [parents, setParents] = useState([]);
    const [studentAccounts, setStudentAccounts] = useState([]);
    const [yearGroup, setYearGroup] = useState('');
    const [subject, setSubject] = useState('');
    const [optionalTopicId, setOptionalTopicId] = useState('');
    const [dayOfWeek, setDayOfWeek] = useState(1);
    const [startsAt, setStartsAt] = useState('18:00');
    const [durationMinutes, setDurationMinutes] = useState(60);
    const [mode, setMode] = useState('online');
    const [zoomLink, setZoomLink] = useState('');
    const [googleClassroomCode, setGoogleClassroomCode] = useState('');

    const [loading, setLoading] = useState(isEdit);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [recurrencePreview, setRecurrencePreview] = useState({}); // termId → dates[]

    // Load all reference data + the class itself if editing.
    useEffect(() => {
        const auth = authHeaders();
        const loaders = [
            axios.get(`${API_URL}/api/admin/academic-years`, auth),
            axios.get(`${API_URL}/api/admin/accounts?role=teacher&accountStatus=approved&limit=200`, auth),
            Promise.all([
                axios.get(`${API_URL}/api/admin/accounts?role=parent&accountStatus=approved&limit=300`, auth),
                axios.get(`${API_URL}/api/admin/accounts?role=student&accountStatus=approved&limit=300`, auth),
            ]),
            axios.get(`${API_URL}/api/admin/optional-topics`, auth),
            axios.get(`${API_URL}/api/subjects?activeOnly=true`),
        ];
        if (isEdit) loaders.push(axios.get(`${API_URL}/api/admin/tutoring-classes/${id}`, auth));

        Promise.all(loaders)
            .then(([y, t, [p, s], top, subs, c]) => {
                setYears(y.data || []);
                setTutors(t.data?.items || []);
                const parents = (p.data?.items || []).map((it) => ({ ...it, _kind: 'Parent' }));
                const sts = (s.data?.items || []).map((it) => ({ ...it, _kind: 'Student' }));
                // Keep the flat combined list for backwards-compat but also
                // expose the split so we can group by family below.
                setStudents([...parents, ...sts]);
                setParents(parents);
                setStudentAccounts(sts);
                setTopics(top.data || []);
                setSubjects(Array.isArray(subs?.data) ? subs.data.map((sub) => sub.name).filter(Boolean) : []);

                if (c) {
                    const cls = c.data || {};
                    setName(cls.name || '');
                    setAcademicYearId(cls.academicYearId || '');
                    setTermIds((cls.termIds || []).map((tt) => tt._id || tt));
                    setTutorId(cls.tutorId?._id || cls.tutorId || '');
                    setStudentIds((cls.studentIds || []).map((st) => st._id || st));
                    setYearGroup(cls.yearGroup || '');
                    setSubject(cls.subject || '');
                    setOptionalTopicId(cls.optionalTopicId?._id || cls.optionalTopicId || '');
                    setDayOfWeek(cls.dayOfWeek ?? 1);
                    setStartsAt(cls.startsAt || '18:00');
                    setDurationMinutes(cls.durationMinutes || 60);
                    setMode(cls.mode || 'online');
                    setZoomLink(cls.zoomLink || '');
                    setGoogleClassroomCode(cls.googleClassroomCode || '');
                } else {
                    // Default to active year + select all its terms
                    const active = (y.data || []).find((yy) => yy.isActive);
                    if (active) setAcademicYearId(active._id);
                }
            })
            .catch((err) => setError(err?.response?.data?.message || 'Failed to load.'))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    // When year changes, load its terms.
    useEffect(() => {
        if (!academicYearId) { setTerms([]); return; }
        axios
            .get(`${API_URL}/api/admin/terms?academicYearId=${academicYearId}`, authHeaders())
            .then((r) => {
                const list = r.data || [];
                setTerms(list);
                // For a brand-new class, default termIds to all terms in the year.
                if (!isEdit) setTermIds(list.map((t) => t._id));
            })
            .catch(() => setTerms([]));
    }, [academicYearId, isEdit]);

    // Live recurrence preview per selected term.
    useEffect(() => {
        if (!termIds.length || dayOfWeek == null) { setRecurrencePreview({}); return; }
        let cancelled = false;
        Promise.all(
            termIds.map((tid) =>
                axios
                    .get(`${API_URL}/api/admin/terms/${tid}/recurrence-preview?dayOfWeek=${dayOfWeek}`, authHeaders())
                    .then((r) => [tid, r.data?.dates || []])
                    .catch(() => [tid, []])
            )
        ).then((pairs) => {
            if (cancelled) return;
            setRecurrencePreview(Object.fromEntries(pairs));
        });
        return () => { cancelled = true; };
    }, [termIds.join(','), dayOfWeek]);

    // Topics are level-agnostic — admin picks whichever fits this class.
    const filteredTopics = topics;

    const toggleTerm = (tid) => {
        setTermIds((arr) => (arr.includes(tid) ? arr.filter((x) => x !== tid) : [...arr, tid]));
    };

    const toggleStudent = (sid) => {
        setStudentIds((arr) => (arr.includes(sid) ? arr.filter((x) => x !== sid) : [...arr, sid]));
    };

    /**
     * Group approved parent + student accounts into "families" so the roster
     * picker shows the parent → linked-students hierarchy rather than a flat
     * jumble. Three buckets:
     *
     *   1. Linked families — a parent with one or more linked student accounts.
     *      Admin ticks either the parent OR the student (family emails will
     *      fan out to both automatically when the class publishes).
     *   2. Parent-only families — a parent with `childInfo` on record but no
     *      linked student account yet (the common case for waiting-list
     *      conversions or existing families we haven't split into two accounts).
     *   3. Un-linked students — a student account with no `parentId` set.
     */
    const families = useMemo(() => {
        const parentById = new Map(parents.map((p) => [String(p._id), p]));
        const studentsByParent = new Map();
        const orphanStudents = [];
        for (const s of studentAccounts) {
            const pid = s.parentId ? String(s.parentId) : null;
            if (pid && parentById.has(pid)) {
                if (!studentsByParent.has(pid)) studentsByParent.set(pid, []);
                studentsByParent.get(pid).push(s);
            } else {
                orphanStudents.push(s);
            }
        }
        const linked = [];
        const parentOnly = [];
        for (const p of parents) {
            const kids = studentsByParent.get(String(p._id)) || [];
            if (kids.length) linked.push({ parent: p, students: kids });
            else parentOnly.push(p);
        }
        return { linked, parentOnly, orphanStudents };
    }, [parents, studentAccounts]);

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            const payload = {
                name: name.trim(),
                academicYearId,
                termIds,
                tutorId,
                studentIds,
                yearGroup: yearGroup || null,
                subject: subject || null,
                optionalTopicId: isYearWithTopics(yearGroup) ? (optionalTopicId || null) : null,
                dayOfWeek,
                startsAt,
                durationMinutes: Number(durationMinutes) || 60,
                mode,
                zoomLink: zoomLink.trim() || null,
                googleClassroomCode: googleClassroomCode.trim() || null,
            };
            if (isEdit) {
                await axios.patch(`${API_URL}/api/admin/tutoring-classes/${id}`, payload, authHeaders());
            } else {
                await axios.post(`${API_URL}/api/admin/tutoring-classes`, payload, authHeaders());
            }
            navigate('/tutoring-classes');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <Container>
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Typography variant="h4" sx={{ mb: 3 }}>
                {isEdit ? 'Edit class' : 'New class'}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ p: 3, mb: 3 }}>
                <Stack spacing={2}>
                    <TextField
                        label="Class name"
                        placeholder="e.g. GCSE English Lit · Monday 6pm"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select size="small" label="Academic year"
                            value={academicYearId}
                            onChange={(e) => setAcademicYearId(e.target.value)}
                            sx={{ flex: 1 }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y._id} value={y._id}>{y.label}{y.isActive ? ' (active)' : ''}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select size="small" label="Tutor"
                            value={tutorId}
                            onChange={(e) => setTutorId(e.target.value)}
                            sx={{ flex: 1 }}
                        >
                            <MenuItem value="">— Select tutor —</MenuItem>
                            {tutors.map((t) => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.firstName} {t.surname} ({t.email})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Terms</Typography>
                        <Stack direction="row" spacing={1}>
                            {terms.map((t) => (
                                <Chip
                                    key={t._id}
                                    label={t.name}
                                    color={termIds.includes(t._id) ? 'primary' : 'default'}
                                    onClick={() => toggleTerm(t._id)}
                                    variant={termIds.includes(t._id) ? 'filled' : 'outlined'}
                                />
                            ))}
                            {terms.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Pick an academic year first.
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            select size="small" label="Day of week"
                            value={dayOfWeek}
                            onChange={(e) => setDayOfWeek(Number(e.target.value))}
                            sx={{ flex: 1 }}
                        >
                            {WEEKDAYS.map((d, i) => (
                                <MenuItem key={i} value={i}>{d}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="time"
                            label="Start time"
                            InputLabelProps={{ shrink: true }}
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            type="number"
                            label="Duration (min)"
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(e.target.value)}
                            size="small"
                            sx={{ width: 140 }}
                            inputProps={{ min: 15, step: 15 }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <YearGroupSelect
                            value={yearGroup}
                            onChange={setYearGroup}
                            anyLabel="— None —"
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            select size="small" label="Subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            sx={{ flex: 1 }}
                        >
                            <MenuItem value="">— None —</MenuItem>
                            {subjects.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </TextField>
                        {isYearWithTopics(yearGroup) && (
                            <TextField
                                select size="small" label="Optional topic"
                                value={optionalTopicId}
                                onChange={(e) => setOptionalTopicId(e.target.value)}
                                sx={{ flex: 1 }}
                                helperText={filteredTopics.length ? '' : 'Add topics in Optional topics page.'}
                            >
                                <MenuItem value="">— None —</MenuItem>
                                {filteredTopics.map((t) => (
                                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>

                    <Divider />

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Students ({studentIds.length} selected)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Tick the student (and parent, if the parent has no linked student account) you
                            want to add to this class's roster. Family emails fan out to both parent and
                            linked students when the class is published, so you don't need to tick both
                            sides of a linked family.<br />
                            <strong>Only approved accounts appear here</strong>{' '}— if someone is missing,
                            check <em>Users → Pending approvals</em> first.<br />
                            <strong>Waiting-list families:</strong> a parent who came in through the public
                            Waiting list only shows up here after admin creates their parent account.
                            On the <em>Waiting list</em> page use <strong>Create account &amp; enrol</strong>{' '}
                            to create the parent account and drop the student onto a class in one step.<br />
                            Publishing a class flips every roster member's engagement to <em>enrolled</em>{' '}
                            (and the tutor's to <em>teaching</em>) automatically; archiving or removing them
                            flips it back to <em>not enrolled</em> if they aren't on any other live class.
                        </Typography>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            <RosterPickerGroups
                                families={families}
                                studentIds={studentIds}
                                toggleStudent={toggleStudent}
                            />
                            {!families.linked.length && !families.parentOnly.length && !families.orphanStudents.length && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                    No approved accounts yet. Add or approve some under Users.
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Divider />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            select size="small" label="Mode"
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            sx={{ flex: 1 }}
                        >
                            {MODES.map((m) => (
                                <MenuItem key={m} value={m}>{MODE_LABEL[m] || m}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Zoom link"
                            value={zoomLink}
                            onChange={(e) => setZoomLink(e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                            placeholder="https://us02web.zoom.us/j/…"
                        />
                        <TextField
                            label="Google Classroom code"
                            value={googleClassroomCode}
                            onChange={(e) => setGoogleClassroomCode(e.target.value)}
                            size="small"
                            sx={{ width: 200 }}
                            placeholder="abc-xyz-pqr"
                        />
                    </Stack>
                </Stack>
            </Card>

            {/* Live recurrence preview */}
            {termIds.length > 0 && (
                <Card sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Lesson dates preview ({WEEKDAYS[dayOfWeek]}s)
                    </Typography>
                    <Stack spacing={2}>
                        {termIds.map((tid) => {
                            const term = terms.find((t) => t._id === tid);
                            const dates = recurrencePreview[tid] || [];
                            return (
                                <Box key={tid}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {term?.name || '—'} · {dates.length} lessons
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {dates.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                No matching dates in this term.
                                            </Typography>
                                        )}
                                        {dates.map((d) => (
                                            <Chip
                                                key={d}
                                                size="small"
                                                label={new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Card>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => navigate('/tutoring-classes')} disabled={busy}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={busy}>
                    {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create as draft'}
                </Button>
            </Box>
        </Container>
    );
}

/**
 * Roster picker rendered as three sections:
 *   1. Linked families — parent row + indented linked-student row(s).
 *   2. Parent-only — parents with childInfo but no linked student account.
 *   3. Unlinked students — student accounts with no `parentId` set.
 *
 * Ticking either the parent OR the student row adds that account to the
 * roster. Family-scoped emails will fan out via `getLinkedRecipients` on
 * publish, so admin doesn't have to tick both.
 */
function RosterPickerGroups({ families, studentIds, toggleStudent }) {
    const rowSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.25,
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
    };
    const sectionHeader = (label) => (
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontWeight: 600,
                mt: 1,
                mb: 0.5,
                px: 1,
            }}
        >
            {label}
        </Typography>
    );
    const row = (acc, opts = {}) => {
        const isParent = acc.role === 'parent';
        const label = isParent
            ? `${acc.firstName || ''} ${acc.surname || ''}`.trim() +
              (acc.childInfo?.studentName ? ` — parent of ${acc.childInfo.studentName}` : '')
            : `${acc.firstName || ''} ${acc.surname || ''}`.trim();
        return (
            <Box
                key={acc._id}
                onClick={() => toggleStudent(acc._id)}
                sx={{ ...rowSx, pl: opts.indent ? 4 : 1 }}
            >
                <Checkbox
                    checked={studentIds.includes(acc._id)}
                    onChange={() => toggleStudent(acc._id)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0.5 }}
                />
                <Typography variant="body2">
                    <strong>{isParent ? 'Parent' : 'Student'}:</strong> {label}
                </Typography>
            </Box>
        );
    };

    // Single-child families collapse into one row so the parent + student
    // sit next to each other; ticking the box adds the student to the roster.
    const singleChildRow = (parent, student) => {
        const parentName = `${parent.firstName || ''} ${parent.surname || ''}`.trim();
        const studentName = `${student.firstName || ''} ${student.surname || ''}`.trim();
        const ticked = studentIds.includes(student._id);
        return (
            <Box
                key={parent._id}
                onClick={() => toggleStudent(student._id)}
                sx={{ ...rowSx, py: 0.5 }}
            >
                <Checkbox
                    checked={ticked}
                    onChange={() => toggleStudent(student._id)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0.5 }}
                />
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                    <strong>Student:</strong> {studentName}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ mx: 1 }}>·</Typography>
                    <strong>Parent:</strong> {parentName}
                </Typography>
            </Box>
        );
    };

    // Multi-child families keep per-student checkboxes so admin can pick
    // individual siblings for different classes. The parent's name sits as
    // a bold header (no checkbox — you don't tick the parent).
    const multiChildFamily = (parent, students) => {
        const parentName = `${parent.firstName || ''} ${parent.surname || ''}`.trim();
        return (
            <Box key={parent._id} sx={{ mb: 0.5, borderLeft: 2, borderColor: 'divider', ml: 0.5 }}>
                <Box sx={{ pl: 1.5, pt: 0.5, pb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>Parent:</strong> {parentName}
                    </Typography>
                </Box>
                {students.map((s) => row(s, { indent: true }))}
            </Box>
        );
    };

    return (
        <>
            {families.linked.length > 0 && sectionHeader('Families')}
            {families.linked.map(({ parent, students }) =>
                students.length === 1
                    ? singleChildRow(parent, students[0])
                    : multiChildFamily(parent, students)
            )}

            {families.parentOnly.length > 0 && sectionHeader('Parents without a linked student account')}
            {families.parentOnly.map((p) => row(p))}

            {families.orphanStudents.length > 0 && sectionHeader('Students without a linked parent')}
            {families.orphanStudents.map((s) => row(s))}
        </>
    );
}
